// Work calendar / attendance (admin). Month grid of expected vs actual per worker, with
// a per-day editor (planned + actual times, status, late/early flags to confirm/decline)
// and a month summary (expected, actual, owed/buffer, worked/absent/late). Statistics Phase 3.
import React, { useEffect, useState, useCallback } from 'react';
import { authFetch, API } from './api';
import HelpPanel from './HelpPanel';
import PlannedDatesPanel from './PlannedDatesPanel';

interface Cell {
  status: string;
  planned_start: string | null; planned_end: string | null; break_min: number | null;
  planned_min: number;
  actual_start: string | null; actual_end: string | null;
  actual_worked_min: number | null; owed_min: number | null;
  flag: string | null; confirmed: boolean; note: string | null;
  reason: string | null; credited: boolean;
  source: string; has_activity: boolean;
}
interface Worker {
  username: string; display_name: string; worker_type: string; country: string;
  active: boolean; default_shift_id: number | null;
  cells: Record<string, Cell>;
  summary: { expected_min: number; actual_min: number; owed_min: number;
             worked_days: number; absent_days: number; late_days: number; vacation_days: number; };
}
interface CalData { year: number; month: number; days: string[]; today: string;
  shifts: { id: number; name: string; start_time: string; end_time: string; unpaid_break_min: number }[];
  workers: Worker[]; }

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
// status -> label. 'working' is normal; credited (paid, owed 0) vs zero (repayable deficit).
const STATUS_OPTS: [string, string][] = [
  ['working', 'Working'],
  ['vacation', 'Vacation (paid)'],
  ['sick_haigusleht', 'Sick — haigusleht (paid)'],
  ['sick_uncertified', 'Sick — no certificate'],
  ['absent', 'Absent'],
  ['day_off', 'Day off'],
  ['holiday', 'Public holiday'],
  ['off', 'Off (non-working)'],
];
// Statuses that auto-set actual (no manual actual entry): credited fills = expected, zero fills = 0.
const CREDITED = ['vacation', 'sick_haigusleht'];
const ZERO = ['absent', 'day_off', 'sick_uncertified'];
// Entering one of these across a range records a proper absence, so it also shows up on the
// Absences tab and counts towards vacation. Rest days and holidays are always skipped for them.
const ABSENCE_STATUSES = [...CREDITED, ...ZERO];
const AUTO_ACTUAL = [...CREDITED, ...ZERO, 'holiday', 'off'];
const REASON_OPTS: [string, string][] = [
  ['', '— pick a reason —'],
  ['not_productive', 'Not productive'],
  ['emergency', 'Emergency'],
  ['left_early', 'Left early'],
  ['other', 'Other'],
];
function toMin(t: string | null | undefined) {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function fmtMin(m: number | null) {
  if (m == null) return '—';
  const sign = m < 0 ? '-' : '';
  const a = Math.abs(m); const h = Math.floor(a / 60); const mm = Math.round(a % 60);
  return `${sign}${h}h ${mm}m`;
}
function addHours(t: string, hours: number) {
  const [h, m] = t.split(':').map(Number);
  let total = h * 60 + m + hours * 60;
  total = ((total % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

export default function WorkCalendar() {
  const now = new Date();
  const [ym, setYm]       = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [data, setData]   = useState<CalData | null>(null);
  const [loading, setLoading] = useState(false);
  const [sel, setSel]     = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, Partial<Cell>>>({});
  const [savingU, setSavingU] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await authFetch(`${API}/statistics/calendar?year=${ym.year}&month=${ym.month}`).then(r => r.json());
      setData(d);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [ym]);
  useEffect(() => { load(); }, [load]);

  const shift = (delta: number) => {
    setSel(null);
    setYm(p => {
      let m = p.month + delta, y = p.year;
      if (m < 1) { m = 12; y--; } if (m > 12) { m = 1; y++; }
      return { year: y, month: m };
    });
  };

  // --- multi-day span entry ---
  const [spanOpen, setSpanOpen] = useState(false);
  const [spanUser, setSpanUser] = useState('');
  const [spanFrom, setSpanFrom] = useState('');
  const [spanTo, setSpanTo] = useState('');
  const [spanStatus, setSpanStatus] = useState('vacation');
  const [spanNote, setSpanNote] = useState('');
  const [spanSkipHol, setSpanSkipHol] = useState(true);
  const [spanSkipNon, setSpanSkipNon] = useState(true);
  const [spanBusy, setSpanBusy] = useState(false);
  const [spanErr, setSpanErr] = useState<string | null>(null);
  const [spanResult, setSpanResult] = useState<{ applied_count: number; absence_id?: number;
    skipped: { date: string; reason: string }[] } | null>(null);
  const spanIsAbsence = ABSENCE_STATUSES.includes(spanStatus);

  const applySpan = async () => {
    if (!spanUser || !spanFrom || !spanTo) { setSpanErr('Pick a person and both dates'); return; }
    setSpanBusy(true); setSpanErr(null); setSpanResult(null);
    const r = await authFetch(`${API}/statistics/calendar/span`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: spanUser, from: spanFrom, to: spanTo, status: spanStatus,
        note: spanNote || null, skip_holidays: spanSkipHol, skip_non_working_days: spanSkipNon,
      }),
    });
    setSpanBusy(false);
    if (!r.ok) {
      let m = `${r.status}`;
      try { m = (await r.json()).detail || m; } catch { /* keep status */ }
      setSpanErr(m); return;
    }
    setSpanResult(await r.json());
    load();
  };

  const openDay = (date: string) => {
    setSel(date);
    const dr: Record<string, Partial<Cell>> = {};
    data?.workers.forEach(w => { dr[w.username] = { ...(w.cells[date] || {}) }; });
    setDraft(dr);
  };
  const setField = (u: string, patch: Partial<Cell>) =>
    setDraft(prev => ({ ...prev, [u]: { ...prev[u], ...patch } }));

  const saveDay = async (w: Worker) => {
    if (!sel) return;
    const c = draft[w.username] || {};
    setSavingU(w.username);
    await authFetch(`${API}/statistics/calendar/day`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: w.username, date: sel, status: c.status || 'working',
        planned_start: c.planned_start || null, planned_end: c.planned_end || null,
        break_min: c.break_min ?? null,
        actual_start: c.actual_start || null, actual_end: c.actual_end || null,
        note: c.note || null, reason: c.reason || null,
      }),
    });
    setSavingU(null);
    await load();
  };

  const decide = async (w: Worker, decision: 'confirm' | 'decline') => {
    if (!sel) return;
    const c = draft[w.username] || {};
    await authFetch(`${API}/statistics/calendar/day/confirm`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: w.username, date: sel, flag: c.flag, decision }),
    });
    await load();
    if (sel) openDay(sel);
  };

  // month grid scaffolding (Mon-first)
  const firstDow = data ? (new Date(ym.year, ym.month - 1, 1).getDay() + 6) % 7 : 0; // 0=Mon
  const daysInMonth = data ? data.days.length : 0;
  const gridCells: (string | null)[] = [];
  for (let i = 0; i < firstDow; i++) gridCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) gridCells.push(`${ym.year}-${String(ym.month).padStart(2, '0')}-${String(d).padStart(2, '0')}`);

  // Everyone whose employment covered this month, INCLUDING people who have since left or
  // been paused — a past month must keep showing who actually worked it.
  const monthWorkers = (data?.workers || []);
  const dayInfo = (date: string) => {
    let scheduled = 0, late = 0, needsEntry = 0, holiday: string | null = null;
    monthWorkers.forEach(w => {
      const c = w.cells[date]; if (!c) return;
      if (c.status === 'working') scheduled++;
      if (c.flag === 'late?' || c.flag === 'late') late++;
      // A past working day with no hours from any source — typed, tracked or assumed.
      if ((c as any).needs_entry) needsEntry++;
      if (c.status === 'holiday' && c.note) holiday = c.note;
    });
    return { scheduled, late, needsEntry, holiday };
  };

  // Whose hours are missing on the selected day, so they can be filled without hunting.
  const missingOn = (date: string) =>
    monthWorkers.filter(w => (w.cells[date] as any)?.needs_entry);

  // Month total, straight from the per-worker summary the API already returns.
  const monthNeedsEntry = (data?.workers || [])
    .reduce((n, w) => n + (((w as any).summary?.needs_entry_days) || 0), 0);

  const inp = "bg-gray-800 border border-gray-700 text-gray-200 text-xs rounded px-2 py-1 focus:outline-none focus:border-blue-500";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => shift(-1)} className="px-2.5 py-1.5 text-sm rounded-lg bg-gray-800 text-gray-400 hover:text-white">←</button>
        <div className="text-white font-semibold text-lg w-44 text-center">{MONTHS[ym.month - 1]} {ym.year}</div>
        <button onClick={() => shift(1)} className="px-2.5 py-1.5 text-sm rounded-lg bg-gray-800 text-gray-400 hover:text-white">→</button>
        {loading && <span className="text-gray-600 text-sm">Loading…</span>}
      </div>

      {monthNeedsEntry > 0 && (
        <div className="bg-[#1a1d27] border border-amber-900/40 rounded-xl px-4 py-3">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-amber-400/90 text-xs uppercase tracking-widest font-semibold">
              {monthNeedsEntry} day(s) need hours entering
            </span>
            <span className="text-[11px] text-gray-500">
              Past working days with no recorded activity — marked amber below. Until they are filled
              in they count as not worked, which mid-migration is usually wrong.
            </span>
          </div>
        </div>
      )}

      {/* Month grid */}
      <div>
        <div className="grid grid-cols-7 gap-1 mb-1 text-[11px] text-gray-500 uppercase tracking-wide">
          {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => <div key={d} className="px-2">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {gridCells.map((date, i) => {
            if (!date) return <div key={i} className="h-20" />;
            const info = dayInfo(date);
            const isToday = data?.today === date;
            const isSel = sel === date;
            const num = parseInt(date.slice(-2));
            return (
              <button key={i} onClick={() => openDay(date)}
                title={info.needsEntry > 0
                  ? `${info.needsEntry} person(s) have no hours recorded for this day`
                  : undefined}
                className={`h-20 text-left p-1.5 rounded-lg border transition-colors ${
                  isSel ? 'border-blue-500 bg-blue-950/30'
                    : info.needsEntry > 0 ? 'border-amber-700/60 bg-amber-950/10 hover:border-amber-600'
                      : 'border-gray-800 bg-[#1a1d27] hover:border-gray-700'}`}>
                <div className={`text-xs font-semibold ${isToday ? 'text-blue-400' : 'text-gray-300'}`}>{num}</div>
                {info.holiday && <div className="mt-0.5 text-[9px] text-red-400 truncate">{info.holiday}</div>}
                {info.scheduled > 0 && <div className="mt-0.5 text-[10px] text-gray-500">{info.scheduled} scheduled</div>}
                {info.needsEntry > 0 && (
                  <div className="mt-0.5 inline-block text-[10px] text-amber-300 bg-amber-950/40 px-1 rounded">
                    {info.needsEntry} no hours
                  </div>
                )}
                {info.late > 0 && <div className="mt-0.5 inline-block text-[10px] text-orange-300 bg-orange-950/40 px-1 rounded">⚠ {info.late} late?</div>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Day editor */}
      {sel && data && (
        <div className="bg-[#1a1d27] rounded-xl border border-gray-800">
          <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-white">{sel}</h3>
              {missingOn(sel).length > 0 && (
                <p className="text-[11px] text-amber-400/80 mt-0.5">
                  No hours recorded for: {missingOn(sel).map(w => w.display_name).join(', ')}
                </p>
              )}
            </div>
            <button onClick={() => setSel(null)} className="text-gray-500 hover:text-white text-sm">Close</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-500 border-b border-gray-800">
                  <th className="px-3 py-2 text-left font-medium">Worker</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-left font-medium">Planned</th>
                  <th className="px-3 py-2 text-left font-medium">Actual</th>
                  <th className="px-3 py-2 text-left font-medium">Owed / flag</th>
                  <th className="px-3 py-2 text-left font-medium">Reason / note</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {monthWorkers.map(w => {
                  const gone = !w.active;
                  const c = draft[w.username] || {};
                  const orig = w.cells[sel];
                  const pending = c.flag && c.flag.endsWith('?');
                  const status = c.status || 'working';
                  const autoActual = AUTO_ACTUAL.includes(status);
                  const plannedMin = orig?.planned_min ?? 0;
                  const actualMin = toMin(c.actual_start) != null && toMin(c.actual_end) != null
                    ? Math.max((toMin(c.actual_end)! - toMin(c.actual_start)!) - (c.break_min ?? orig?.break_min ?? 0), 0)
                    : null;
                  // A working day with actual below expected needs a reason for the accountant.
                  const needsReason = status === 'working' && actualMin != null && plannedMin > 0 && actualMin < plannedMin;
                  return (
                    <tr key={w.username} className="border-b border-gray-800/50 align-top">
                      <td className="px-3 py-2 text-gray-200 whitespace-nowrap">{w.display_name}
                        {gone && (
                          <span className="ml-2 text-[10px] text-gray-500 uppercase"
                                title="No longer with us — kept here because they worked this month">
                            left
                          </span>
                        )}
                        <span className="ml-1.5 text-[9px] uppercase text-gray-600">{w.worker_type}</span></td>
                      <td className="px-3 py-2">
                        <select className={inp} value={status} onChange={e => setField(w.username, { status: e.target.value })}>
                          {STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                        {CREDITED.includes(status) && <div className="text-[9px] text-green-500/70 mt-1">paid · counts as worked</div>}
                        {ZERO.includes(status) && <div className="text-[9px] text-red-400/70 mt-1">away · owed hours</div>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <input type="time" className={`${inp} w-24`} value={c.planned_start || ''}
                          onChange={e => setField(w.username, { planned_start: e.target.value, planned_end: c.planned_end || (e.target.value ? addHours(e.target.value, 8.5) : null) })} />
                        <span className="text-gray-600 mx-1">–</span>
                        <input type="time" className={`${inp} w-24`} value={c.planned_end || ''} onChange={e => setField(w.username, { planned_end: e.target.value })} />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {autoActual ? (
                          <span className="text-gray-600 italic">{CREDITED.includes(status) ? 'paid = planned' : 'auto 0'}</span>
                        ) : (
                          <>
                            <input type="time" className={`${inp} w-24`} value={c.actual_start || ''} onChange={e => setField(w.username, { actual_start: e.target.value })} />
                            <span className="text-gray-600 mx-1">–</span>
                            <input type="time" className={`${inp} w-24`} value={c.actual_end || ''} onChange={e => setField(w.username, { actual_end: e.target.value })} />
                            {orig?.has_activity && orig?.source !== 'manual' && <span className="ml-1 text-[9px] text-gray-600">auto</span>}
                          </>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`${(orig?.owed_min ?? 0) < 0 ? 'text-red-400' : 'text-green-400'}`}>{fmtMin(orig?.owed_min ?? null)}</span>
                        {pending && (
                          <span className="ml-2 inline-flex items-center gap-1">
                            <span className="text-orange-300 bg-orange-950/40 px-1 rounded">{c.flag}</span>
                            <button onClick={() => decide(w, 'confirm')} className="text-green-400 hover:text-green-300">✓</button>
                            <button onClick={() => decide(w, 'decline')} className="text-gray-500 hover:text-gray-300">✗</button>
                          </span>
                        )}
                        {c.flag && !pending && <span className="ml-2 text-red-300 bg-red-950/40 px-1 rounded">{c.flag}</span>}
                      </td>
                      <td className="px-3 py-2">
                        {needsReason && (
                          <select className={`${inp} mb-1 ${c.reason ? '' : 'border-orange-700/60'}`} value={c.reason || ''}
                            onChange={e => setField(w.username, { reason: e.target.value })}>
                            {REASON_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                        )}
                        <input className={`${inp} w-40 block`} placeholder="note…" value={c.note || ''}
                          onChange={e => setField(w.username, { note: e.target.value })} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => saveDay(w)} className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded">{savingU === w.username ? '…' : 'Save'}</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Month summary */}
      <div>
        <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-2 px-1">Month summary</div>
        <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-xs border-b border-gray-800">
                <th className="px-3 py-2 text-left font-medium">Worker</th>
                <th className="px-3 py-2 text-right font-medium">Expected</th>
                <th className="px-3 py-2 text-right font-medium">Actual</th>
                <th className="px-3 py-2 text-right font-medium">Owed / buffer</th>
                <th className="px-3 py-2 text-right font-medium">Worked</th>
                <th className="px-3 py-2 text-right font-medium">Absent</th>
                <th className="px-3 py-2 text-right font-medium">Late</th>
              </tr>
            </thead>
            <tbody>
              {(data?.workers || []).map(w => (
                <tr key={w.username} className={`border-b border-gray-800/50 ${w.active ? '' : 'opacity-40'}`}>
                  <td className="px-3 py-2.5 text-gray-200">{w.display_name}
                    <span className="ml-1.5 text-[9px] uppercase text-gray-600">{w.worker_type}</span></td>
                  <td className="px-3 py-2.5 text-right text-gray-300">{fmtMin(w.summary.expected_min)}</td>
                  <td className="px-3 py-2.5 text-right text-gray-300">{fmtMin(w.summary.actual_min)}</td>
                  <td className={`px-3 py-2.5 text-right font-medium ${w.summary.owed_min < 0 ? 'text-red-400' : 'text-green-400'}`}>{fmtMin(w.summary.owed_min)}</td>
                  <td className="px-3 py-2.5 text-right text-gray-400">{w.summary.worked_days}</td>
                  <td className="px-3 py-2.5 text-right">{w.summary.absent_days > 0 ? <span className="text-red-400">{w.summary.absent_days}</span> : <span className="text-gray-700">—</span>}</td>
                  <td className="px-3 py-2.5 text-right">{w.summary.late_days > 0 ? <span className="text-orange-400">{w.summary.late_days}</span> : <span className="text-gray-700">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-600 mt-2">Actual hours for warehouse workers are inferred from tracked activity (first→last of the day) and can be corrected per day above. Office workers are entered manually.</p>
      </div>

      {/* Multi-day span entry */}
      <div className="mt-4">
        <button onClick={() => { setSpanOpen(o => !o); setSpanErr(null); setSpanResult(null); }}
          className="text-xs text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 px-3 py-1.5 rounded-lg">
          {spanOpen ? '▾' : '▸'} Enter a date range (vacation, sick leave…)
        </button>

        {spanOpen && (
          <div className="mt-2 bg-[#1a1d27] border border-gray-800 rounded-xl p-4">
            <p className="text-xs text-gray-500 mb-3">
              Applies one status across a whole range in a single action. Weekends, non-contracted days
              and public holidays are skipped, and nothing is written outside the person's employment.
              {spanIsAbsence && (
                <span className="text-gray-400"> This is an absence, so it is recorded as a proper
                  absence too — it will appear on the Absences tab, and vacation counts towards the
                  balance automatically.</span>
              )}
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-xs text-gray-500">Person
                <select className={`${inp} ml-2`} value={spanUser} onChange={e => setSpanUser(e.target.value)}>
                  <option value="">Select…</option>
                  {data?.workers.map(w => <option key={w.username} value={w.username}>{w.display_name}</option>)}
                </select>
              </label>
              <label className="text-xs text-gray-500">From
                <input type="date" className={`${inp} ml-2`} value={spanFrom} onChange={e => setSpanFrom(e.target.value)} />
              </label>
              <label className="text-xs text-gray-500">To
                <input type="date" className={`${inp} ml-2`} value={spanTo} onChange={e => setSpanTo(e.target.value)} />
              </label>
              <label className="text-xs text-gray-500">Status
                <select className={`${inp} ml-2`} value={spanStatus} onChange={e => setSpanStatus(e.target.value)}>
                  {STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-3">
              {/* Leave never consumes a day the person was not going to work, so for an absence
                  these are not choices to make — shown disabled rather than hidden, so the rule
                  is visible instead of the controls just vanishing. */}
              <label className={`flex items-center gap-2 text-xs ${spanIsAbsence ? 'text-gray-600' : 'text-gray-400'}`}
                     title={spanIsAbsence ? 'Always skipped for an absence' : undefined}>
                <input type="checkbox" disabled={spanIsAbsence} checked={spanIsAbsence || spanSkipNon}
                  onChange={e => setSpanSkipNon(e.target.checked)} />
                Skip non-working days
              </label>
              <label className={`flex items-center gap-2 text-xs ${spanIsAbsence ? 'text-gray-600' : 'text-gray-400'}`}
                     title={spanIsAbsence ? 'Always skipped for an absence' : undefined}>
                <input type="checkbox" disabled={spanIsAbsence} checked={spanIsAbsence || spanSkipHol}
                  onChange={e => setSpanSkipHol(e.target.checked)} />
                Skip public holidays
              </label>
              <input className={`${inp} flex-1 min-w-[160px]`} value={spanNote}
                onChange={e => setSpanNote(e.target.value)} placeholder="Note (optional)" />
              <button onClick={applySpan} disabled={spanBusy}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs rounded-lg font-medium">
                {spanBusy ? 'Applying…' : 'Apply to range'}
              </button>
            </div>
            {spanErr && <p className="text-xs text-red-400 mt-2">{spanErr}</p>}
            {spanResult && (
              <div className="mt-3 pt-3 border-t border-gray-800 text-xs">
                <span className="text-green-400">{spanResult.applied_count} day(s) set.</span>
                {spanResult.absence_id && (
                  <span className="text-gray-400"> Recorded as absence #{spanResult.absence_id} —
                    open the Absences tab to add how they told you, or a certificate.</span>
                )}
                {spanResult.skipped.length > 0 && (
                  <span className="text-gray-500"> Skipped {spanResult.skipped.length}:{' '}
                    {spanResult.skipped.map(x => `${x.date.slice(5)} (${x.reason})`).join(', ')}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Agreed dates — only meaningful for planned_dates contracts, so the panel says so
          when nobody is on one. Follows the month currently shown in the grid above. */}
      <PlannedDatesPanel year={ym.year} month={ym.month} />

      <HelpPanel items={[
        { label: 'N days need hours entering / amber days', desc: 'A past working day where somebody has an expected shift, no absence recorded, and no hours from any source — not typed in, not derived from scanning, not assumed from their shift. The engine has to treat those as not worked, so while the WMS/WGR migration is in progress (and for offline warehouse tasks) they are usually just missing data. Open the day and the people concerned are named at the top.' },
        { label: 'Agreed dates', desc: 'For people on an \'Agreed dates\' contract: pick the specific days they are expected to work this month. Those are the only days that carry expected hours — every other day expects nothing and owes nothing. A day already marked vacation or sick is never overwritten.' },
        { label: 'Enter a date range', desc: 'Sets one status across many days at once — a two-week vacation is one action instead of fourteen. It lists exactly what it skipped and refuses to write outside the person\'s employment period. If the status is an absence it also records a proper absence, so the same fortnight off appears on the Absences tab and counts towards the vacation balance; there is no second place to enter it. Rest days and public holidays are always skipped for an absence, since leave does not consume a day nobody was going to work.' },
        { label: '← / → (month)', desc: 'Switch months. Past months are frozen: changing a shift template later never moves already-passed days.' },
        { label: 'Day cell', desc: 'Click any day to open the per-worker editor below. A cell shows how many are scheduled, any public holiday, and a ⚠ count of unreviewed late arrivals.' },
        { label: 'Status', desc: 'Working = a normal day. Vacation & Sick—haigusleht are PAID (count as worked). Absent, Day off & Sick—no certificate are unpaid — the hours are owed and must be worked back. Public holiday / Off need no hours.' },
        { label: 'Planned', desc: 'The expected shift, pre-filled from the worker\'s default shift. Expected hours never drop because of an absence reason — they\'re contractual.' },
        { label: 'Actual', desc: 'For paid/away statuses this is automatic (paid = planned, away = 0). For a working day, warehouse hours come from tracked activity (editable) and office hours are entered here.' },
        { label: 'Reason / note', desc: 'If a working day\'s actual is below planned, pick a reason (why the hours are short) — it feeds the accountant export. The note is free text and can be added to any day.' },
        { label: 'Owed / flag', desc: 'Owed = actual − expected for the day. A detected late/early arrival shows a ⚠ flag with ✓ (confirm) / ✗ (dismiss); once reviewed it stops re-appearing.' },
        { label: 'Save', desc: 'Saves that one worker\'s day. Always press Save — nothing is stored until you do.' },
      ]} />
    </div>
  );
}
